import React, { memo, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import {
  useResponsive,
  useFormattedValue,
  useBubblePosition,
  useTrackColor,
} from "../hooks";

/**
 * RangeSlider with magnetic edge bubbles that stick to edges on mobile
 */
const RangeSlider = ({
  label,
  bubbleLabel,
  min,
  max,
  step,
  value,
  onChange,
  getPercentage,
  bubbleSuffix = "",
  formatCurrency = false,
  bubbleClassName = "",
}) => {
  // Refs for measurements
  const containerRef = useRef(null);
  const bubbleRef = useRef(null);

  // Custom hooks
  const isMobile = useResponsive();
  const rawPercentage = getPercentage(value, min, max);
  const bubblePosition = useBubblePosition(
    containerRef,
    bubbleRef,
    rawPercentage,
    isMobile
  );
  const formattedValue = useFormattedValue(value, formatCurrency);
  const trackColor = useTrackColor(bubbleClassName);

  // Generate unique ID for accessibility
  const sliderId = React.useId
    ? React.useId()
    : `slider-${label?.replace(/\s+/g, "-").toLowerCase() || "range"}`;

  return (
    <div className="mrc-slider-group">
      {label && (
        <label htmlFor={sliderId} className="mrc-slider-label">
          {label}
        </label>
      )}
      <div
        className="mrc-slider-container"
        ref={containerRef}
        style={{
          "--bubble-left-percent": bubblePosition,
          "--value-percent": rawPercentage,
          "--track-color": trackColor,
        }}
      >
        <input
          id={sliderId}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value}
          aria-label={label || bubbleLabel}
        />
        <div className={`mrc-slider-bubble ${bubbleClassName}`} ref={bubbleRef}>
          <span className="mrc-slider-bubble-label">{bubbleLabel}</span>
          <span className="mrc-slider-bubble-value">
            {formattedValue} {bubbleSuffix}
          </span>
        </div>
        <div className={`mrc-slider-thumb-overlay ${bubbleClassName}`} />
      </div>
    </div>
  );
};

// PropTypes for type checking
RangeSlider.propTypes = {
  label: PropTypes.string,
  bubbleLabel: PropTypes.string,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  getPercentage: PropTypes.func.isRequired,
  bubbleSuffix: PropTypes.string,
  formatCurrency: PropTypes.bool,
  bubbleClassName: PropTypes.string,
};

export default memo(RangeSlider);
